import { useEffect } from 'react'
import { getTokens, setTokens } from '@/auth/tokenStore'
import { onTokensUpdated } from '@/features/auth/utils/authBroadcast'

const REFRESH_LEAD_MS = 60 * 1000
const VISIBILITY_MARGIN_MS = 2 * 60 * 1000

type TriggerRefresh = () => Promise<void> | void

export function useProactiveRefresh(triggerRefresh: TriggerRefresh): void {
  useEffect(() => {
    if (typeof window === 'undefined') return

    let timerId: ReturnType<typeof setTimeout> | null = null
    let cancelled = false

    const schedule = (): void => {
      if (timerId !== null) {
        clearTimeout(timerId)
        timerId = null
      }
      const tokens = getTokens()
      if (!tokens) return
      const delay = Math.max(0, tokens.access_token_expiry - Date.now() - REFRESH_LEAD_MS)
      timerId = setTimeout(() => { void fire() }, delay)
    }

    const fire = async (): Promise<void> => {
      if (cancelled) return
      try {
        await triggerRefresh()
      } finally {
        if (!cancelled) schedule()
      }
    }

    const onVisibility = (): void => {
      if (document.visibilityState !== 'visible') return
      const tokens = getTokens()
      if (!tokens) return
      const remaining = tokens.access_token_expiry - Date.now()
      if (remaining < VISIBILITY_MARGIN_MS) {
        if (timerId !== null) {
          clearTimeout(timerId)
          timerId = null
        }
        timerId = setTimeout(() => { void fire() }, 0)
      } else {
        schedule()
      }
    }

    const unsubscribe = onTokensUpdated((next) => {
      setTokens(next)
      schedule()
    })

    schedule()
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      cancelled = true
      if (timerId !== null) clearTimeout(timerId)
      document.removeEventListener('visibilitychange', onVisibility)
      unsubscribe()
    }
  }, [triggerRefresh])
}
